import React, { FunctionComponent } from 'react'
import { Col, Row } from 'react-grid-system'
import { useTranslation, Trans } from 'react-i18next'

import {
  StyledProductSearchContainer,
  StyledProductSearchItem,
  StyledProductSearchItemContentLeft,
  StyledProductSearchItemContentRight,
  StyledProductSearchPillButton,
  StyledProductSearchPagination,
  StyledProductSearchHeader,
  StyledPageTitle,
  StyledBasicSearch,
  StyledProductSearchCounter,
  SearchPageHeader,
  SearchPageContent,
  StyledHeaderClinPageContentFrame,
  StyledProductBrandText,
  StyledAlternativesMessage,
  StyledFilter,
  StyledFilterWrapper,
  StyledAreaFilterActions
} from './ProductSearch.styles'
import { ClinTheme } from '../../../ClinTheme'
import { ClinText } from '../../../components/ClinText'
import {
  getCurrentPage,
  getTotalPages,
  IPagination
} from '../../../components/ClinPagination/ClinPagination.model'
import { ClinSpinner } from '../../../components/ClinSpinner'
import { StyledSpinnerContainer } from '../../../components/ClinSpinner/ClinSpinner.styles'
import { SortDirectionType } from '../../../components/ClinTableOrderToggle/ClinTableOrderToggle'
import {
  UserRoleRecord,
  UserRole,
  isAusMaUser,
  isAusGaUser
} from '../../../constants'
import { useAppContext } from '../../../context/app'
import { useCheckUnableHomePage } from '../../../hooks/useCheckUnableHomePage/useCheckUnableHomePage'
import { IProductCatalog } from '../../../types'
import {
  CatalogDto,
  FacetDto,
  ProductSearchDto
} from '../../../types/swaggerTypes'
import {
  ProductColumn,
  productFilterOptions,
  ProductFilterOptions
} from '../Product.models'
import { NotFound } from '../../../features/NotFound'
import { ClinIcon } from '../../../components/ClinIcon'
import { ClinIconPathName } from '../../../components/ClinIcon/ClinIcon.paths'
import { ClinPageContentFrame } from '../../../components/ClinPageContentFrame'
import { ClinChip } from '../../../components/ClinChip'
import { ClinButton } from '../../../components/ClinButton'
import { ChipState } from '../../../components/ClinChip/ClinChip.styles'
import { ClinSpacer } from '../../../components/ClinSpacer/ClinSpacer'
import analyticsServiceSingleton from '../../../services/Analytics/initAnalytics'
import { TableFilterSearch } from '../../../features/TableFilterSearch'

interface IProductSearchProps {
  /** Whether table is loading or not */
  isLoading?: boolean
  /** User type */
  isMaUser?: boolean
  /** The initial query */
  initialQuery?: string
  /** The array of products search results */
  products?: CatalogDto[]
  /** The array of therapeutic areas */
  areaFilters?: FacetDto[]
  /** The array of therapeutic areas selected */
  selectedAreaFilters?: FacetDto[]
  /** The array of availability values */
  availabilityFilters?: FacetDto[]
  /** The selected availability values */
  selectedAvailabilityFilters?: FacetDto[]
  /** Selected filter options for chip-based filters */
  selectedFilterOptions?: string[]
  /** Columns to show on the orders table */
  columns?: ProductColumn[]
  /** The selected column */
  selectedSortColumn?: ProductColumn
  /** The selected sort direction */
  selectedSortDirection?: SortDirectionType
  /** Pagination */
  pagination?: IPagination
  /** User country*/
  userCountry?: string
  /** Current search parameters */
  searchParams?: ProductSearchDto
  /** Whether the current results are from an alias search */
  isAliasSearchResult?: boolean
  /** When table row clicked */
  handleRowClicked?: (itemId: string | number, isProgram: boolean) => void
  /** When pagination page is selected */
  handlePageClicked?: (selectedPageIndex: number) => void
  /** Select results per page */
  handlePageSizeChange?: (pageSize: number) => void
  /** When a filter is selected */
  handleFilterToggle?: (filter: FacetDto, filterGroupTitle: string) => void
  /** Clear all therapeutic area filters */
  handleClearAreaFilters?: () => void
  /** Select the toggle sort column */
  handleToggleSort?: (selectedColumn: ProductColumn) => void
  /** When a search query is entered and ENTER pressed */
  handleOnChange: (query: string) => void
  /** When a filter option is selected */
  handleSetSelectedFilterOption?: (option: string) => void
  /** Toggle the opening of the selected row */
  handleToggleProductRow: (productId: string) => void
}

export const ProductSearch: FunctionComponent<IProductSearchProps> = ({
  isLoading,
  isMaUser,
  initialQuery,
  products,
  pagination,
  userCountry,
  isAliasSearchResult,
  selectedFilterOptions,
  areaFilters,
  selectedAreaFilters,
  handleRowClicked,
  handlePageClicked,
  handlePageSizeChange,
  handleOnChange,
  handleSetSelectedFilterOption,
  handleFilterToggle,
  handleClearAreaFilters
}) => {
  const { t } = useTranslation()
  const { enableNewHomePage } = useCheckUnableHomePage()
  const { userRole } = useAppContext()
  return (
    <>
      <ClinPageContentFrame
        className="search-page-container"
        isFullWidth={true}
        crumbs={
          isMaUser && !isAusMaUser(userCountry!, userRole) && enableNewHomePage
            ? []
            : [
                isAusGaUser(userCountry!, userRole) && enableNewHomePage
                  ? {
                      path: '/home',
                      name: t('navigation:home')
                    }
                  : {
                      path: '/orders',
                      name: t('product_catalogue:breadcrumbs.previous_page')
                    },
                {
                  name: t('product_catalogue:breadcrumbs.current_page'),
                  path: '/products'
                }
              ]
        }
      >
        <SearchPageHeader>
          <StyledHeaderClinPageContentFrame>
            <Row>
              <Col sm={12}>
                <StyledProductSearchHeader>
                  <StyledPageTitle>
                    {t('product_catalogue:non_ma_title')}
                  </StyledPageTitle>
                  <StyledBasicSearch
                    initialQuery={initialQuery}
                    placeholder={
                      userRole && UserRoleRecord[userRole as UserRole].isMaUser
                        ? t('product_catalogue:search.ma_placeholder')
                        : t('product_catalogue:search.non_ma_placeholder')
                    }
                    debounceTimeMs={500}
                    handleOnChange={handleOnChange}
                  />
                </StyledProductSearchHeader>
              </Col>
            </Row>
          </StyledHeaderClinPageContentFrame>
        </SearchPageHeader>

        <SearchPageContent>
          <ClinPageContentFrame
            isFullWidth={false}
            className="search-page-container-inner"
          >
            <Row>
              <Col>
                <ClinSpacer height={26} />
                <StyledFilterWrapper>
                  <StyledFilter>
                    <ClinText
                      marginRight={`${ClinTheme.space[3]}px`}
                      lineHeight={ClinTheme.lineHeights.small}
                      color={ClinTheme.colors.black}
                      fontSize={ClinTheme.fontSizes[1]}
                      fontWeight={ClinTheme.fontWeights.bold}
                    >
                      Filter by
                    </ClinText>
                    {productFilterOptions?.map((filter, index) => {
                      const isActive =
                        (selectedFilterOptions?.findIndex(
                          (i) => i === filter.optionId
                        ) ?? -1) > -1

                      const handleClick = () => {
                        handleSetSelectedFilterOption?.(filter.optionId)

                        analyticsServiceSingleton.trackEvent(filter.event, {
                          filterId: filter.optionId,
                          filterName: filter.mixPanelLabel,
                          isActive: !isActive
                        })
                      }

                      return (
                        <ClinChip
                          style={{ padding: '8px 16px' }}
                          key={index}
                          state={
                            isActive ? ChipState.Active : ChipState.Default
                          }
                          isInteractive={true}
                          onClick={handleClick}
                        >
                          {filter.optionValue}
                        </ClinChip>
                      )
                    })}
                  </StyledFilter>
                </StyledFilterWrapper>
                <ClinSpacer height={24} hasBorder={true} />
              </Col>
            </Row>
            {selectedFilterOptions?.includes(
              ProductFilterOptions.TherapeuticAreas
            ) && (
              <Row>
                <Col sm={12}>
                  {(selectedAreaFilters?.length ?? 0) > 0 && (
                    <StyledAreaFilterActions>
                      <ClinButton
                        variant="linkButton"
                        onClick={handleClearAreaFilters}
                      >
                        {t('common:buttons.clear_all')}
                      </ClinButton>
                    </StyledAreaFilterActions>
                  )}
                  <TableFilterSearch
                    showTitle={false}
                    showAll={true}
                    displayBorder={false}
                    displaySeeButton={false}
                    displayClearButton={false}
                    alwaysExpanded={true}
                    displaySearch={false}
                    filterGroupStyles={{
                      padding: `0 0 12px 0`,
                      marginBottom: ClinTheme.space[5],
                      borderBottom: `solid ${ClinTheme.borderWidths[0]}px ${ClinTheme.colors.midGrey}`
                    }}
                    displayFilterIcon={false}
                    title={t('product_catalogue:filter.title')}
                    filterGroups={[
                      {
                        title: t('product_catalogue:filter.group_selected'),
                        titleSelected: `${t(
                          'product_catalogue:filter.group_selected'
                        )} (${selectedAreaFilters?.length}) ${t(
                          'product_catalogue:filter.selected'
                        )}`,
                        filters: areaFilters,
                        selectedFilters: selectedAreaFilters,
                        displayCounts: true
                      }
                    ]}
                    handleSelectFilterGroup={(filter, filterGroupTitle) =>
                      handleFilterToggle &&
                      handleFilterToggle(filter, filterGroupTitle)
                    }
                    handleClearAllFilters={handleClearAreaFilters}
                    searchInput={() => null}
                  />
                </Col>
              </Row>
            )}
            <Row>
              <Col sm={12}>
                {isLoading || products === undefined ? (
                  <StyledSpinnerContainer>
                    <ClinSpinner size={ClinTheme.space[7]} />
                  </StyledSpinnerContainer>
                ) : (
                  <>
                    {isAliasSearchResult &&
                      products.length > 0 &&
                      initialQuery && (
                        <StyledAlternativesMessage>
                          <ClinIcon
                            iconName={ClinIconPathName.InfoOutline}
                            iconSize={24}
                            iconFill={ClinTheme.colors.primaryLight}
                          />
                          <ClinText fontSize={ClinTheme.fontSizes[2]}>
                            <Trans
                              i18nKey="product_catalogue:alternatives"
                              values={{ query: initialQuery }}
                              components={[<strong key="query-highlight" />]}
                            />
                          </ClinText>
                        </StyledAlternativesMessage>
                      )}
                    {products.length > 0 ? (
                      <StyledProductSearchContainer>
                        {products.map(
                          (product: IProductCatalog, index: number) => {
                            const isProgram =
                              product.derivedFrom === `ManagedAccessProgram`
                            const itemId = isProgram
                              ? product.programId
                              : product.catalogDocumentId
                            return (
                              <StyledProductSearchItem
                                onClick={() => {
                                  handleRowClicked &&
                                    handleRowClicked(itemId, isProgram)
                                }}
                                key={`product_${itemId}_${index}`}
                              >
                                <StyledProductSearchItemContentLeft>
                                  <ClinText
                                    style={{
                                      position: 'relative'
                                    }}
                                    as="h3"
                                    fontWeight={ClinTheme.fontWeights.bold}
                                    fontSize={ClinTheme.fontSizes[1]}
                                    lineHeight={'24px'}
                                    width="fit-content"
                                  >
                                    {product.catalogItemName}
                                    <StyledProductSearchCounter>
                                      {isProgram ? 1 : product.items.length}
                                    </StyledProductSearchCounter>
                                  </ClinText>
                                  <StyledProductBrandText as="p">
                                    {product.brands.join(', ')}
                                  </StyledProductBrandText>
                                </StyledProductSearchItemContentLeft>
                                <StyledProductSearchItemContentRight>
                                  {product.programStatus && (
                                    <StyledProductSearchPillButton
                                      isProgram={isProgram}
                                    >
                                      {product.programStatus}
                                    </StyledProductSearchPillButton>
                                  )}
                                  <StyledProductSearchPillButton
                                    isProgram={isProgram}
                                  >
                                    {isProgram ? 'Access program' : ' Product'}
                                  </StyledProductSearchPillButton>
                                  <ClinIcon
                                    iconSize={16}
                                    iconFill={ClinTheme.colors.primaryMid}
                                    iconName={ClinIconPathName.ChevronRight}
                                  />
                                </StyledProductSearchItemContentRight>
                              </StyledProductSearchItem>
                            )
                          }
                        )}
                      </StyledProductSearchContainer>
                    ) : (
                      <NotFound searchInput={initialQuery} />
                    )}
                  </>
                )}
              </Col>
            </Row>
            <Row>
              <Col>
                {/* Portfolio filtering runs after server pagination, so the
                    current page can be empty while later results still exist. */}
                {!isLoading && pagination && pagination.total > 0 && (
                  <StyledProductSearchPagination
                    showLeftSpacer
                    classNameForPaginationSelector="pagination-select"
                    classNameForPaginationContainer="pagination-container"
                    classNameForPreviousButton="pagination-previous-button"
                    classNameForNextButton="pagination-next-button"
                    classNameForResultsPerPage="pagination-results-per-page"
                    currentPage={getCurrentPage(pagination)}
                    totalPages={getTotalPages(pagination)}
                    pageSize={pagination.take}
                    handlePageSelected={(pageIndex) =>
                      handlePageClicked && handlePageClicked(pageIndex)
                    }
                    handlePageSizeChange={handlePageSizeChange}
                  />
                )}
              </Col>
            </Row>
          </ClinPageContentFrame>
        </SearchPageContent>
      </ClinPageContentFrame>
    </>
  )
}
